import { ChangeEvent, Fragment, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';

import axiosInstance from '../../services/axios/axios';
import Edit from './containers/Edit';
import FormEntry from './components/FormEntry';
import UARLayout from './containers/Layout';
import { Headline5 } from '../../components/UI/MDC/Typography/Typography';
import List, {
	ListItem,
	MDCListItemGraphic,
} from '../../components/UI/MDC/List/List';
import Icon from '../../components/UI/MDC/Icon/Icon';
import { Headline6 } from '../../components/UI/MDC/Typography/Typography';
import { CustomTypes } from '../../components/UI/Input';
import { WrapImsPacket } from '../../store/utility';
import * as ancillaryActions from '../../store/ancillary/actions';
import * as uarActions from '../../store/uar/actions';
import classes from '../../components/UI/Input/Textbox/Textbox.module.scss';
import SubmissionSummary from './containers/SubmissionSummary';
import { SUBMISSION_LOADING_TITLE } from './util/constants';

const Reassign = props => {
	/**
	 * Extract needed variables from the props provided by the `Scaffold`
	 */
	let {
		dispatch,
		loggedInUser,
		error,
		setError,
		errorMessage,
		loading,
		profileDetails,
		bulkSelections,
		canPerformActions,
	} = props;

	/**
	 * [Redux State] - bulk list of targets to validate
	 */
	const bulkSelectionsFromState = useSelector(
		state => state.uar.bulkSelections
	);

	/**
	 * (set)Submitted - flag to indicate if submit button has been clicked
	 */
	const [submitted, setSubmitted] = useState(false);
	/**
	 * (set)SubmittedSuccessfully - flag to indicate if submission was successful
	 */
	const [submittedSuccessfully, setSubmittedSuccessfully] = useState(false);

	const [searchResults, setSearchResults] = useState([]);

	/**
	 * set(Form) - data for input form
	 */
	const [form, setForm] = useState({
		newOwner: {
			label: 'Search for new owner',
			value: {},
			visible: true,
			validation: {
				valid: true,
				nonManagerSelected: false,
				message: '',
				rules: {
					required: true,
					maxLength: 75,
				},
				touched: false,
			},
			settings: {
				readOnly: true,
				type: CustomTypes.UPMCESOSEARCH,
			},
		},
	});

	useEffect(() => {
		if (bulkSelectionsFromState) {
			let adminDetected = [];

			// eslint-disable-next-line react-hooks/exhaustive-deps
			bulkSelections = bulkSelectionsFromState;
			bulkSelections.forEach(selection => {
				if (selection.administrative) {
					dispatch(
						uarActions.removeBulkSelection({
							payload: selection,
						})
					);
					adminDetected.push(selection.eso);
				}
			});
			if (adminDetected.length > 0) {
				const removedMembers = adminDetected.join(', ');
				dispatch(ancillaryActions.clearMessages());
				dispatch(
					ancillaryActions.addMessage({
						Key: 2,
						Value: `This action, Reassign Account(s), is not allowed for administrative accounts.  All admin accounts have been removed from your bulk request: ${removedMembers}`,
					})
				);
			}
		}
	}, [bulkSelectionsFromState]);

	/**
	 * when profile information becomes available, populate the form(s)
	 */
	useEffect(() => {
		if (profileDetails) {
			let newForm = { ...form };
			for (let key in newForm) {
				if (key in profileDetails) {
					newForm[key].value = profileDetails[key];
				}
			}
			setForm(newForm);
			// validate();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [profileDetails]);

	//FUNCTIONS
	/**
	 * event handler for form input changes
	 * @param {ChangeEvent<HTMLInputElement>} event
	 * @param {string} key
	 */
	const handleChange = (event, key) => {
		const selection = event.target.value;
		if (key in form && selection) {
			let newForm = { ...form };
			if (selection.isManager) {
				newForm[key].value = selection;
				newForm[key].nonManagerSelected = false;
			} else {
				newForm[key].nonManagerSelected = true;
			}
			setForm(newForm);
		}
		validate();
	};

	/**
	 * actions to take upon form submission
	 */
	const submitHandler = () => {
		const isValid = validate();
		if (isValid) {
			setSubmitted(true);
			let targets = [];
			if (bulkSelections && bulkSelections.length > 0) {
				targets = bulkSelections;
			} else {
				targets.push(profileDetails);
			}
			const payload = {
				newOwner: form.newOwner.value,
				loggedInUser: loggedInUser.loggedInUser,
				targets: targets,
			};
			axiosInstance
				.post(
					'/api/UserAccessReview/Reassignment',
					WrapImsPacket(payload)
				)
				.then(response => {
					switch (response.status) {
						case 200:
							if (response.data.responseCode) {
								const responseCode = response.data.responseCode;
								switch (responseCode) {
									case 200:
										setSubmittedSuccessfully(true);
										break;
									default:
										setSubmitted(false);
										setSubmittedSuccessfully(false);
										break;
								}
							}
							break;

						default:
							setSubmitted(false);
							setSubmittedSuccessfully(false);
							break;
					}
				})
				.catch(errors => {
					setSubmitted(false);
					setSubmittedSuccessfully(false);
				});
		} else {
			dispatch(
				ancillaryActions.addMessage({
					Key: 2,
					Value: 'Please correct the validation issues as indicated below.',
				})
			);
		}
		//TODO|BPK: else post message
	};

	/**
	 * actions to take if cancelled
	 */
	const cancelHandler = () => {
		props.history.goBack();
	};

	const clearSearchHandler = () => {
		setSearchResults([]);

		const newForm = { ...form };
		newForm.newOwner.value = {};
		setForm(newForm);
	};

	const searchHandler = payload => {
		clearSearchHandler();

		const modifiedPayload = { ...payload, isPrimary: true };

		axiosInstance
			.post(
				'/api/UserAccessReview/EsoSearch',
				WrapImsPacket(modifiedPayload)
			)
			.then(response => {
				switch (response.status) {
					case 200:
						if (response.data.responseCode) {
							const responseCode = response.data.responseCode;
							let data = response.data.payload;
							switch (responseCode) {
								case 200:
									setSearchResults(data);
									break;
								default:
									break;
							}
						}
						break;

					default:
						break;
				}
			})
			.catch(error => {
				setError(true);
			});
	};

	/**
	 * form validation logic
	 * @param {ChangeEvent<HTMLInputElement>} event
	 */
	const validate = event => {
		const touchedKey =
			event && event.target && event.target.id ? event.target.id : null;
		let newForm = { ...form };
		let allValid = true;
		for (let key in newForm) {
			let valid = false;
			let value = newForm[key].value;
			let message = '';

			switch (key) {
				case 'newOwner':
					//ESO Search selection
					valid = value && value.esoId;
					if (newForm[key].nonManagerSelected) {
						message =
							'The owner you selected is not a manager.  Please select a manager to proceed.';
					} else if (!valid) {
						message = 'No owner selected.';
					}
					break;
				default:
					break;
			}
			newForm[key].validation.valid = valid;
			newForm[key].validation.message = message;
			newForm[key].validation.touched = true;

			//no `touchedKey` indicates a submission event
			if (!touchedKey) {
				newForm[key].validation.touched = true;
			}
			allValid = allValid && valid;

			//exempt if invisible
			valid = newForm[key].visible ? valid : true;
			allValid = allValid && valid;
		}

		//presence of `touchedKey` indicates an input focus-change event
		if (touchedKey) {
			newForm[touchedKey].validation.touched = true;
		}

		setForm(newForm);
		return allValid;
	};

	//RENDER
	/**
	 * Retrieve settings from state and generate the input component
	 * @param {string} key
	 */
	const generateFormInput = (key, type) => {
		let formInput = null;
		if (key in form && form[key].visible) {
			formInput = (
				<FormEntry
					entryKey={key}
					formDetails={form[key]}
					handleChange={e => handleChange(e, key)}
					searchChange={searchHandler}
					clearChange={clearSearchHandler}
					searchResults={searchResults}
					inputType={
						form[key].settings && form[key].settings.type
							? form[key].settings.type
							: 'text'
					}
					readOnly={
						(form[key].settings && form[key].settings.readOnly) ||
						submittedSuccessfully
					}
				/>
			);
		}
		return formInput;
	};

	const generateSelectionBanner = () => {
		const newOwnerValue = form.newOwner.value;
		return Object.entries(newOwnerValue).length > 0 ? (
			<Fragment>
				<Headline5>Currently Selected Owner</Headline5>
				<List twoLine dense xlarge>
					{newOwnerValue ? (
						<ListItem key={newOwnerValue.esoId}>
							<MDCListItemGraphic
								graphic={<Icon icon='account_circle' />}
							/>
							<div className='mdc-list-item__text'>
								<div className='mdc-list-item__primary-text'>
									<u>{`${newOwnerValue.lastName}, ${newOwnerValue.firstName}`}</u>
								</div>
								<div className='mdc-list-item__secondary-text'>
									<b>Network ID</b>: {newOwnerValue.ntid}{' '}
									<b>Job Title</b>: {newOwnerValue.jobTitle}{' '}
									<b>Department</b>:{' '}
									{newOwnerValue.department}{' '}
								</div>
							</div>
						</ListItem>
					) : null}
				</List>
			</Fragment>
		) : null;
	};

	/**
	 * Reassign form components
	 */
	const formOutput = (
		<>
			{/* New Owner */}
			{generateFormInput('newOwner')}
			{/* Selection */}
			{generateSelectionBanner()}
			{/* Validation*/}

			<div className={classes['input__validation--invalid']}>
				<Headline6>{form.newOwner.validation.message}</Headline6>
			</div>
		</>
	);

	const formOutputSummary = <>{generateSelectionBanner()}</>;
	/**
	 * return JSX to render
	 */
	return (
		<UARLayout
			title='Reassign User'
			loading={loading}
			error={error}
			errorMessage={errorMessage}
			canPerformActions={canPerformActions}
		>
			{submittedSuccessfully ? (
				<SubmissionSummary
					profile={
						bulkSelections && bulkSelections.length > 0
							? null
							: profileDetails
					}
				>
					{formOutputSummary}
				</SubmissionSummary>
			) : (
				<Edit
					cancellable={{
						handler: cancelHandler,
						disabled: submitted,
					}}
					submittable={{
						handler: submitHandler,
						label: 'Continue',
						disabled: submitted,
					}}
					loading={{
						isLoading: submitted,
						title: SUBMISSION_LOADING_TITLE,
						message: null,
					}}
					profile={
						bulkSelections && bulkSelections.length > 0
							? null
							: profileDetails
					}
				>
					{formOutput}
				</Edit>
			)}
		</UARLayout>
	);
};

export default Reassign;
